import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { api } from '../lib/api';
import { SpinnerIcon } from '../onboarding/icons';
import type { OnboardingStatus } from '../onboarding/types';

/**
 * Status-aware root redirect.
 *
 * The SPA root `/` has to choose between `/onboarding` (setup wizard),
 * `/dashboard` (admin console), and `/login` (M20 — the entry point for a
 * logged-out user). The decision is based on whether the tenant has connected
 * Stripe AND (QA-F #5) whether the historical import is still running:
 *
 *   stripeConnected === false                  → /onboarding
 *   stripeConnected === true, import running   → /onboarding (locked)
 *   stripeConnected === true, import done/idle → /dashboard
 *   401 / error (no session)                   → /login (M20: re-auth via OAuth)
 *
 * Stripe Connect + import completion are the two gates for the dashboard:
 * while the bootstrap is in flight the wizard stays locked to the onboarding
 * progress screen so the user never sees a half-populated dashboard.
 *
 * Routing logic lives client-side (here) rather than in /auth/start so there's
 * exactly one source of truth for the decision; /auth/start just mints the
 * session cookie and bounces to `/`.
 */
export function RootRedirect() {
  const [target, setTarget] = useState<'onboarding' | 'dashboard' | 'login' | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get<OnboardingStatus>('onboarding/status');
        if (cancelled) return;
        if (!data.stripeConnected) {
          setTarget('onboarding');
          return;
        }
        // Stripe is connected — check whether the import is still running.
        // Best-effort: if the workflow-status call fails, default to the
        // dashboard (don't block the user on a transient fetch error).
        try {
          const wf = await api.get<{
            missing: boolean;
            status: string;
            progress?: { phase?: string; } | null;
          }>('onboarding/bootstrap/workflow-status');
          if (cancelled) return;
          const completed =
            wf.status === 'complete' || wf.progress?.phase === 'done';
          const running =
            !wf.missing &&
            !completed &&
            wf.status !== 'errored' &&
            wf.status !== 'terminated';
          setTarget(running ? 'onboarding' : 'dashboard');
          return;
        } catch {
          /* fall through — can't confirm import state, allow dashboard */
        }
        setTarget('dashboard');
      } catch (e: unknown) {
        if (cancelled) return;
        // 401 (no/invalid session) or any fetch error → go to the login page
        // (M20). The login flow runs HubSpot OAuth; if the app isn't installed
        // yet the callback takes the install path, then bounces back here.
        // Never land the user on a blank root.
        void e;
        setTarget('login');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (target === null) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 text-ink-500">
        <SpinnerIcon className="h-6 w-6 text-brand-500" />
        <p className="text-sm">Loading…</p>
      </div>
    );
  }

  return <Navigate to={target} replace />;
}
